import { ActionIcon, Button, Grid, Group, Paper, Text } from '@mantine/core';
import { Dropzone, DropzoneStatus, MIME_TYPES } from '@mantine/dropzone';
import { useRef } from 'react';

import { formList } from '@mantine/form';
import { UseFormReturnType } from '@mantine/form/lib/use-form';
import { showNotification } from '@mantine/notifications';
import { Trash, X } from 'tabler-icons-react';
import { BusinessFileNames } from '../../../enums/business-file-names';
import { SettlementFileNames } from '../../../enums/settlement-file-names';
import handleFileRejection from '../../../helpers/common.helpers';
import { UpdateFormFile } from '../../../type/file.types';
import './file-upload.styles.css';

interface Props {
  form: UseFormReturnType<any>;
  value: BusinessFileNames | SettlementFileNames;
  dataCy?: string;
}
function BaseFileUpload(props: Props) {
  const { form, value } = props;
  const openRef = useRef<() => void>();

  const MAX_FILE_SIZE_BYTES = 26214400;
  const MAX_FILE_SIZE_MB = MAX_FILE_SIZE_BYTES / 1048576;

  const MAX_LENGTH = 20;

  const dropzoneChildren = (status: DropzoneStatus) => (
    <Group
      position='center'
      spacing='xl'
      style={{ pointerEvents: 'none', display: 'none' }}
    >
      <div>
        <Text size='xl' inline>
          Drag images here or click to select files
        </Text>
        <Text size='sm' color='dimmed' inline mt={7}>
          Attach as many files as you like, each file should not exceed 5mb
        </Text>
      </div>
    </Group>
  );

  const getFileName = (formFile: UpdateFormFile | File | undefined) => {
    if (formFile instanceof File) {
      return formFile.name;
    } else if (formFile?.file instanceof File) {
      return formFile.file.name;
    }
  };

  return (
    <>
      <Paper withBorder p={5}>
        <Grid align='center'>
          <Grid.Col xs={12} md={6}>
            <Button
              onClick={() => {
                openRef.current && openRef.current();
              }}
              color='gray'
              fullWidth
            >
              <Text style={{ fontSize: 14 }}>Select File</Text>
            </Button>
          </Grid.Col>

          <Dropzone
            // @ts-ignore
            openRef={openRef}
            className='hidden-file-upload-button'
            maxSize={MAX_FILE_SIZE_BYTES}
            accept={[MIME_TYPES.png, MIME_TYPES.jpeg, MIME_TYPES.pdf]}
            onReject={(fileRejections) => {
              handleFileRejection(fileRejections, MAX_FILE_SIZE_MB);
            }}
            onDrop={(files: File[]) => {
              if (files.length + form.values[value].length > MAX_LENGTH) {
                showNotification({
                  id: 'max-file-uploads-reached',
                  message: `Cannot upload more than ${MAX_LENGTH} files`,
                  icon: <X size={16} />,
                  color: 'red',
                  autoClose: 10000,
                });
                return;
              }

              let droppedFiles: UpdateFormFile[] = files.map((file) => {
                return {
                  id: null,
                  file: file,
                };
              });

              form.setFieldValue(
                value,
                formList([...form.values[value], ...droppedFiles])
              );
            }}
            data-cy={value}
          >
            {(status) => dropzoneChildren(status)}
          </Dropzone>
        </Grid>
      </Paper>
      <Text
        className='truncate'
        style={{ fontSize: 12 }}
        color='gray'
        align='left'
        px={5}
        data-cy={'filesUploaded' + value}
      >
        {typeof form.values?.[value][0]?.file.name === 'string'
          ? form.values[value]?.map(
              (formFile: UpdateFormFile | File | undefined, index: number) => (
                <Group
                  key={index}
                  direction='row'
                  align='flex-end'
                  spacing='xs'
                  noWrap
                >
                  <ActionIcon
                    color='red'
                    variant='hover'
                    onClick={() => form.removeListItem(value, index)}
                    data-cy='removeFile'
                  >
                    <Trash />
                  </ActionIcon>
                  <Text
                    className='truncate'
                    style={{ fontSize: 14 }}
                    color='gray'
                    align='left'
                  >
                    {getFileName(formFile)}
                  </Text>
                </Group>
              )
            )
          : ' '}
      </Text>
    </>
  );
}

export default BaseFileUpload;
